import React, { createContext, useContext, useState, useCallback } from 'react';

interface SidebarCollapseContextType {
  /** Effective collapse state: the user's own preference, or a page override. */
  isCollapsed: boolean;
  toggleCollapse: () => void;
  /**
   * Ask for the nav to be collapsed for as long as the current page is mounted.
   * Does NOT touch the stored preference — visiting a page must not silently
   * change the nav everywhere else. Always pair with releasePageCollapse in an
   * effect cleanup.
   */
  requestPageCollapse: () => void;
  releasePageCollapse: () => void;
}

const STORAGE_KEY = 'sidebar_collapsed';

/**
 * Set once the user manually expands the nav while a page override is active.
 * Their action wins for the rest of the session, so a page that auto-collapses
 * stops fighting them when they navigate back to it.
 *
 * Module-scoped rather than state so it survives the page unmounting and
 * remounting, and deliberately not persisted: a fresh load starts over.
 */
let userOverrodePageCollapse = false;

/** Test-only reset for the session veto, which is module state by design. */
export const __resetPageCollapseVetoForTests = () => {
  userOverrodePageCollapse = false;
};

const SidebarCollapseContext = createContext<SidebarCollapseContextType | undefined>(undefined);

export const SidebarCollapseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userCollapsed, setUserCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  /**
   * Counted rather than boolean: React double-invokes effects in StrictMode, and
   * during a route change the next page can mount before the previous one
   * unmounts. A boolean would strand the override on in both cases.
   */
  const [pageCollapseCount, setPageCollapseCount] = useState(0);
  const [vetoed, setVetoed] = useState(userOverrodePageCollapse);

  const pageWantsCollapse = pageCollapseCount > 0 && !vetoed;
  const isCollapsed = userCollapsed || pageWantsCollapse;

  const toggleCollapse = useCallback(() => {
    // While a page override is active the nav is collapsed no matter what the
    // stored preference says, so a click can only mean "expand". Treat it as a
    // veto of the override and do not write storage — the user never chose
    // "expanded" globally.
    //
    // This is keyed on the override alone, not on !userCollapsed. Keying it on
    // the preference let a user who already preferred collapsed fall through to
    // the branch below, where the toggle silently flipped their stored setting
    // while the nav stayed visibly put.
    if (pageWantsCollapse) {
      userOverrodePageCollapse = true;
      setVetoed(true);
      // Someone whose own preference is collapsed would snap straight back to
      // collapsed once the override lifts, so clear that too. The click has to
      // do something visible.
      if (userCollapsed) {
        setUserCollapsed(false);
        try { localStorage.setItem(STORAGE_KEY, 'false'); } catch {}
      }
      return;
    }

    setUserCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, String(next)); } catch {}
      return next;
    });
  }, [pageWantsCollapse, userCollapsed]);

  const requestPageCollapse = useCallback(() => setPageCollapseCount(c => c + 1), []);
  const releasePageCollapse = useCallback(() => setPageCollapseCount(c => Math.max(0, c - 1)), []);

  return (
    <SidebarCollapseContext.Provider
      value={{ isCollapsed, toggleCollapse, requestPageCollapse, releasePageCollapse }}
    >
      {children}
    </SidebarCollapseContext.Provider>
  );
};

export const useSidebarCollapse = () => {
  const context = useContext(SidebarCollapseContext);
  if (!context) {
    throw new Error('useSidebarCollapse must be used within SidebarCollapseProvider');
  }
  return context;
};
